/**
 * One ai_template's administration card: active version, append-only prompt
 * editor, model alias, and version history / rollback.
 *
 * EXTRACTED FROM VisitNoteTypeCard, which said in its own header that "nothing
 * here is visit-note-specific except the key". That turned out to be load-bearing
 * rather than a nicety: the canonical visit-note prompts
 * (`visit-note.canonical.<format>[.<standard>]`) need exactly this card and have
 * no visit_note_settings row of their own to administer, so the template half had
 * to become reusable on its own. VisitNoteTypeCard is now this card plus a
 * data-source footer.
 *
 * `footer` is rendered inside the same <section> so a caller can attach the
 * per-note-type toggles without nesting one bordered card inside another.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";

import {
  AI_TEMPLATE_QUERY,
  AI_TEMPLATE_VERSIONS_QUERY,
  PUBLISH_AI_TEMPLATE_VERSION_MUTATION,
  SET_ACTIVE_AI_TEMPLATE_VERSION_MUTATION,
  type AiTemplateResult,
  type AiTemplateVars,
  type AiTemplateVersionsResult,
  type AiTemplateVersionsVars,
  type PublishAiTemplateVersionResult,
  type PublishAiTemplateVersionVars,
  type SetActiveAiTemplateVersionResult,
  type SetActiveAiTemplateVersionVars,
} from "../graphql/adminOps";
import { VisitNotePromptEditor } from "./VisitNotePromptEditor";

export interface AiPromptTemplateCardProps {
  /** ai_template.key this card binds to. */
  templateKey: string;
  /** Heading shown for the card. */
  title: string;
  /** Namespaces the editor's DOM ids — must be unique per rendered instance. */
  idPrefix: string;
  /** data-testid on the <section>. */
  testId: string;
  /** Aliases the server accepts (aiModelAliases). */
  modelAliases: string[];
  /**
   * Migration filename to name in the missing-template warning. The advice has to
   * be the file that actually seeds THIS key — pointing an admin at the wrong one
   * makes them apply a seed that cannot fix what they are looking at.
   */
  seedMigration: string;
  /** Toast label, e.g. "SOAP". Defaults to `title`. */
  publishLabel?: string;
  /** Extra copy under the header, before any warning. */
  subtitle?: ReactNode;
  /** Rendered at the foot of the same section. */
  footer?: ReactNode;
}

export function AiPromptTemplateCard({
  templateKey,
  title,
  idPrefix,
  testId,
  modelAliases,
  seedMigration,
  publishLabel,
  subtitle,
  footer,
}: AiPromptTemplateCardProps) {
  const [showHistory, setShowHistory] = useState(false);

  const {
    data,
    loading,
    error: templateError,
    refetch,
  } = useQuery<AiTemplateResult, AiTemplateVars>(AI_TEMPLATE_QUERY, {
    variables: { key: templateKey },
  });
  const template = data?.aiTemplate ?? null;
  const activeVersion = template?.activeVersion ?? null;

  // Three distinct states used to collapse into one "the template is missing,
  // apply the SQL" warning, because `error` was never inspected:
  //   1. the query FAILED       -> we do not know anything; say so, offer retry.
  //      Telling an on-call admin to re-apply the seed here is actively harmful.
  //   2. the template is ABSENT -> the seed genuinely has not been applied.
  //   3. the template EXISTS but has no active version (active_version_id is
  //      nullable and ON DELETE SET NULL) -> the server is on its fallback and
  //      the fix is to publish a first version, right here.
  const templateMissing = !loading && !templateError && !template;
  const noActiveVersion =
    !loading && !templateError && !!template && !activeVersion;

  // 4. the template EXISTS and has an active version, but the TEMPLATE is
  //    disabled. `findActiveTemplateByKey` filters `is_active = true`, so
  //    generation silently falls back to the server's built-in prompt while this
  //    card would otherwise render as perfectly healthy — name, version badge,
  //    model chip, editable body, working Publish. The card already fetches
  //    `isActive`; it just never showed it.
  const templateDisabled =
    !loading && !templateError && !!template && !template.isActive;

  const { data: versionsData } = useQuery<
    AiTemplateVersionsResult,
    AiTemplateVersionsVars
  >(AI_TEMPLATE_VERSIONS_QUERY, {
    variables: { templateId: template?.id ?? "" },
    skip: !template?.id || !showHistory,
  });

  const [publishVersion, { loading: publishing }] = useMutation<
    PublishAiTemplateVersionResult,
    PublishAiTemplateVersionVars
  >(PUBLISH_AI_TEMPLATE_VERSION_MUTATION);

  const [setActiveVersion, { loading: activating }] = useMutation<
    SetActiveAiTemplateVersionResult,
    SetActiveAiTemplateVersionVars
  >(SET_ACTIVE_AI_TEMPLATE_VERSION_MUTATION);

  const handlePublish = async ({
    instructions,
    model,
    notes,
  }: {
    instructions: string;
    model: string;
    notes: string;
  }) => {
    if (!template) return;
    try {
      await publishVersion({
        variables: {
          templateId: template.id,
          instructions,
          model: model || undefined,
          notes: notes || undefined,
          setActive: true,
        },
      });
      await refetch();
      toast.success(`${publishLabel ?? title}: new version published.`);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to publish a new version",
      );
    }
  };

  const handleActivate = async (versionId: string) => {
    if (!template) return;
    try {
      await setActiveVersion({
        variables: { templateId: template.id, versionId },
      });
      await refetch();
      toast.success("Active version updated.");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to change the active version",
      );
    }
  };

  return (
    <section
      data-testid={testId}
      className="rounded-xl border border-border bg-card px-4 py-3.5 flex flex-col gap-4"
    >
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-sm font-semibold m-0">{title}</h2>
        {template && (
          <span className="text-xs text-muted-foreground">{template.name}</span>
        )}
        {activeVersion && (
          <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium">
            v{activeVersion.version}
          </span>
        )}
        {templateDisabled && (
          <span
            data-testid="template-disabled-badge"
            className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"
          >
            Disabled
          </span>
        )}
        {activeVersion?.model && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium">
            {activeVersion.model}
          </span>
        )}
        <span className="grow" />
        {template && (
          <button
            type="button"
            data-testid="toggle-history"
            onClick={() => setShowHistory((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-accent transition-colors"
          >
            <History className="w-3.5 h-3.5" aria-hidden="true" />
            Version history
          </button>
        )}
      </div>

      {subtitle && (
        <p className="m-0 text-[11px] text-muted-foreground">{subtitle}</p>
      )}

      {/* Query failed — we know nothing about the template. Do NOT advise SQL. */}
      {!loading && templateError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2">
          <AlertTriangle
            className="w-4 h-4 mt-0.5 text-destructive shrink-0"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="m-0 text-xs text-destructive">
              Couldn&apos;t load the <code>{templateKey}</code> template —{" "}
              {templateError.message}
            </p>
            <button
              type="button"
              data-testid="retry-template"
              onClick={() => void refetch()}
              className="mt-1.5 rounded-lg border border-border px-2.5 py-1 text-[11px] font-medium hover:bg-accent transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Missing template — the server will silently use its built-in constant. */}
      {templateMissing && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2">
          <AlertTriangle
            className="w-4 h-4 mt-0.5 text-destructive shrink-0"
            aria-hidden="true"
          />
          <p className="m-0 text-xs text-destructive">
            No <code>{templateKey}</code> template in this environment — the
            server is using its built-in fallback prompt. Apply{" "}
            <code>sql/{seedMigration}</code> to make this prompt editable here.
          </p>
        </div>
      )}

      {/* Template exists, but nothing is published/active for it. */}
      {noActiveVersion && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2">
          <AlertTriangle
            className="w-4 h-4 mt-0.5 text-destructive shrink-0"
            aria-hidden="true"
          />
          <p className="m-0 text-xs text-destructive">
            The <code>{templateKey}</code> template has no active version — the
            server is using its built-in fallback prompt. Publish a first version
            below, or restore one from Version history.
          </p>
        </div>
      )}

      {/* Template disabled — publishing here changes nothing the server reads. */}
      {templateDisabled && (
        <div
          data-testid="template-disabled-banner"
          className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2"
        >
          <AlertTriangle
            className="w-4 h-4 mt-0.5 text-destructive shrink-0"
            aria-hidden="true"
          />
          <p className="m-0 text-xs text-destructive">
            The <code>{templateKey}</code> template is disabled, so the server is
            using its built-in fallback prompt — nothing published here is read
            until it is enabled again on the AI Templates page (tick “Show
            disabled” to find it).
          </p>
        </div>
      )}

      {/* Prompt editor. Also rendered with an EMPTY draft when the template has
          no active version, so the admin can actually author a first one —
          otherwise the card is a dead end: header, history button and toggles
          with no editor and no explanation. */}
      {template && (
        <VisitNotePromptEditor
          idPrefix={idPrefix}
          instructions={activeVersion?.instructions ?? ""}
          model={activeVersion?.model ?? null}
          modelAliases={modelAliases}
          saving={publishing}
          onPublish={handlePublish}
        />
      )}

      {/* Version history */}
      {showHistory && template && (
        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold m-0 text-muted-foreground">
            Version history
          </h3>
          {(versionsData?.aiTemplateVersions ?? []).map((v) => {
            const isActive = v.id === activeVersion?.id;
            return (
              <div
                key={v.id}
                className="flex items-center gap-3 rounded-lg border border-border px-3 py-2"
              >
                <span className="text-xs font-medium">v{v.version}</span>
                <span className="text-[11px] text-muted-foreground truncate grow">
                  {v.notes ?? "—"}
                  {v.createdBy ? ` · ${v.createdBy}` : ""}
                </span>
                {isActive ? (
                  <span className="text-[10px] font-medium text-primary">
                    Active
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={activating}
                    onClick={() => void handleActivate(v.id)}
                    className="rounded-lg border border-border px-2.5 py-1 text-[11px] font-medium hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Make active
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {footer}
    </section>
  );
}

export default AiPromptTemplateCard;
